import React, {
  memo,
  useCallback,
  useEffect,
  useRef
} from 'react';

import useElementEvent from '@/hooks/useElementEvent';
import useBooleanState from '@/hooks/useBooleanState';

import FriendForm, { FriendFormContent } from '@/components/Forms/FriendForm';
import Button from '@/components/UI/Button';
import Modal from '@/components/UI/Modal';
import Icon from '@/components/UI/Icon';

interface FriendFormModalProps {
  lang: string;
  objectId: string;
  button: string;
  content: FriendFormContent;
  recaptchaSiteKey?: string;
  recaptchaDisabled?: boolean;
}

const CLOSE_TIMEOUT = 10000;

const FriendFormModal = ({
  lang,
  objectId,
  button,
  content,
  recaptchaSiteKey,
  recaptchaDisabled
}: FriendFormModalProps) => {
  const timeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const {
    isOpen,
    open: openModal,
    close: closeModal
  } = useBooleanState();

  const onSuccess = useCallback(() => {
    timeout.current = setTimeout(closeModal, CLOSE_TIMEOUT);
  }, [
    closeModal
  ]);

  useElementEvent(`#${button}`, openModal);

  useEffect(() => {
    if (!isOpen) {
      clearTimeout(timeout.current);
    }
  }, [isOpen]);

  return (
    <Modal
      className="share-modal"
      isOpen={isOpen}
      onRequestClose={closeModal}
    >
      <div className="share-modal-content">
        <h3 className="content-subtitle">
          {content.title}
        </h3>

        <FriendForm
          content={content}
          lang={lang}
          objectId={objectId}
          recaptchaSiteKey={recaptchaSiteKey}
          recaptchaDisabled={recaptchaDisabled}
          onSuccess={onSuccess}
        />
      </div>

      <Button
        aria-label="Close"
        className="ReactModal__Close"
        onClick={closeModal}
      >
        <Icon
          color="#afafaf"
          name="Close"
          width={32}
          height={32}
        />
      </Button>
    </Modal>
  );
};

export default memo(FriendFormModal);
